import ProfileIcon from 'Icons/Profile';
import fr from 'facet-rest';

import ForumIcon from '@material-ui/icons/Forum';
import SettingsIcon from '@material-ui/icons/Settings';
import ListIcon from '@material-ui/icons/List';

import DescriptionIcon from '@material-ui/icons/Description';
import {
  adminRoot,
  advisorHouseholdScheduledMessagesUri,
  advisorLandingUri,
  advisorPaths,
  advisorWhatIfPageUri,
  assumptionsAdminUri,
  featuresUri,
  genericPageURI,
  householdBlueprintUri,
  householdEditRecordPersonalInformationUri,
  householdNewPrintUri,
  householdPlansUri,
  householdSecureFilesUri,
  householdTimelineUri,
  settingsUri,
  taskBoardUri,
} from 'Consts/advisorUris';
import {
  advisorBulkRolloverRecommendationQuestionnaires,
  advisorClientExperiencePath,
  advisorClientExperienceSavesPath,
  advisorHouseholdBlueprint,
  advisorHouseholdQuestionnairePath,
  advisorHouseholdSnapshot,
  advisorHouseholdTimelinePath,
  advisorIdentityVerificationReviews,
  advisorMessagesPath,
  advisorPage,
  advisorPageMessages,
  advisorPlanPath,
  advisorSecureFilesPath,
  advisorTemplateManagerPage, advisorWhatIfPage,
} from 'Consts/Strings';
import { hasRelationship } from 'Helpers/Affordances';
import relationships from 'Consts/Relationships';
import ClientExperience from 'Icons/ClientExperience';
import { AccountBalanceWallet, Assessment, Lock } from '@material-ui/icons';
import ClientSearchBar from 'Components/AdvisorApp/Header/ClientSearchBar';
import MessagesBadge from 'Components/UI/Navigation/Badges/MessagesBadge';
import PostAddIcon from '@material-ui/icons/PostAdd';
import AncillaryServicesItem from 'Components/UI/Navigation/CustomItem/AncillaryServicesItem';
import { buildAdvisorLink, buildPageLink } from 'Views/Page/Content/Templates';
import InvestmentsBadge from 'Components/UI/Navigation/Badges/InvestmentsBadge';
import ModelUpdateRequestItem from 'Components/UI/Navigation/CustomItem/ModelUpdateRequestItem';
import { missionControlURL } from 'Hooks/useMissionControlURL';
import SelectedClientItem from '../CustomItem/SelectedClientItem';
import { pathMatches } from '../utils';
import TaskListItem from '../CustomItem/TaskListItem';

const {
  clients,
  investments,
  tasks,
  reporting,
  analytics,
  preferences,
  accountRequests,
  meetingSummary,
  scheduledMessages,
  curatedContentArticles,
} = advisorPaths;

const getBalanceSheetLink = ({ householdResource }) => householdResource?.withRelationship(
  fr.relationships.householdBalanceSheetPage.name,
  (rel) => buildPageLink(rel.link('related'), advisorPage, { householdId: householdResource?.id() }),
  () => undefined,
);

const isRoadmapExplorer = ({ location }) => pathMatches(advisorPage)({ location }) && (location.search.includes('guidedHouseholdReview') || location.search.includes('roadmap-explorer'));
const isPM2Journey = ({ location }) => pathMatches(advisorPage)({ location }) && location.search.includes('pm2Journey');
const isRoadmapDeliverable = ({ location }) => location.search.includes('roadmapDeliverable');
export const hasBetterNav = (...args) => isRoadmapExplorer(...args) || isPM2Journey(...args) || isRoadmapDeliverable(...args);

const advisorSchema = {
  homeLinkPath: adminRoot,
  MobileSearchComponent: ClientSearchBar,
  navigationItems: [
    // New Page context messages
    {
      key: 'messages',
      variant: ({ agentResource }) => {
        return !hasRelationship(agentResource, fr.relationships.allScheduledMessages.name) ? 'link' : 'dropdown';
      },
      label: 'Messages',
      to: ({ agentResource }) => {
        return buildPageLink(
          agentResource?.relationship(fr.relationships.messagesPage.name).link('related'),
          advisorPageMessages,
        );
      },
      test: 'messages',
      isSelected: pathMatches(advisorPageMessages, { isExact: false }) || pathMatches(scheduledMessages),
      icon: ForumIcon,
      BadgeComponent: MessagesBadge,
      hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.messagesPage.name),
      subLinks: [
        {
          key: 'scheduled-messages',
          variant: 'link',
          label: 'Scheduled Messages',
          to: ({ agentResource }) => advisorHouseholdScheduledMessagesUri(agentResource?.relationship(fr.relationships.allScheduledMessages.name).link('related').url()),
          isSelected: pathMatches(scheduledMessages),
          BadgeComponent: MessagesBadge,
          hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.allScheduledMessages.name),
        },
      ],
    },
    {
      key: 'clients',
      variant: 'dropdown',
      label: 'Clients',
      to: clients,
      isSelected: pathMatches(clients),
      icon: ProfileIcon,
      disabled: ({ userResource }) => {
        const isHouseholdSummaryEnabled = hasRelationship(userResource, fr.relationships.householdSummary.name);
        return !isHouseholdSummaryEnabled;
      },
      test: 'clients',
      subLinks: [
        {
          key: 'client-selected',
          variant: 'custom',
          component: SelectedClientItem,
          isSelected: pathMatches(advisorLandingUri),
          hidden: ({ householdResource }) => !householdResource,
        },
        {
          key: 'client-ancillary-services',
          variant: 'custom',
          component: AncillaryServicesItem,
          isSelected: pathMatches(advisorLandingUri),
          hidden: ({ householdResource }) => !householdResource,
        },
        {
          key: 'member-dashboard',
          variant: 'link',
          label: 'Member Dashboard',
          to: ({ householdResource }) => {
            return householdResource?.withRelationship(fr.relationships.memberDashboard.name, (rel) => {
              return buildAdvisorLink(
                rel.link('related'),
                { householdId: householdResource?.attr('householdId') },
              );
            }, () => {
              return '/';
            });
          },
          isSelected: ({ location }) => {
            return location.search.includes('member-dashboard');
          },
          hidden: ({ householdResource }) => {
            if (!householdResource) {
              return true;
            }

            return householdResource?.withRelationship(fr.relationships.memberDashboard.name, () => {
              return false;
            }, () => {
              return true;
            });
          },
        },
        {
          key: 'client-mission-control',
          variant: 'link',
          label: 'Mission Control',
          to: ({ householdResource }) => missionControlURL(householdResource, householdResource?.attr('householdId')),
          isSelected: ({ location }) => location.search.includes('mission-control'),
          hidden: ({ householdResource }) => !missionControlURL(householdResource, householdResource?.attr('householdId')),
        },
        {
          key: 'tax-diagnostic',
          variant: 'link',
          label: 'Tax Diagnostic',
          isSelected: ({ location }) => {
            return location.search.includes('tax-diagnostic');
          },
          to: ({ householdResource }) => {
            return householdResource?.withRelationship(fr.relationships.taxDiagnosticPage.name, (rel) => {
              return buildPageLink(
                rel.link('related'),
                advisorPage,
                { householdId: householdResource?.attr('householdId') },
              );
            }, () => {
              return '/';
            });
          },
          hidden: ({ householdResource }) => {
            if (!householdResource) {
              return true;
            }

            return householdResource?.withRelationship(fr.relationships.taxDiagnosticPage.name, () => {
              return false;
            }, () => {
              return true;
            });
          },
        },
        {
          key: 'transitions-dashboard',
          variant: 'link',
          label: 'Dashboard',
          test: 'client-transitions-dashboard',
          isSelected: ({ location }) => {
            return location.search.includes('transitions-dashboard-page');
          },
          to: ({ householdResource }) => {
            return householdResource?.withRelationship(fr.relationships.transitionsDashboardPage.name, (rel) => {
              return buildPageLink(
                rel.link('related'),
                advisorPage,
                { householdId: householdResource?.attr('householdId') },
              );
            }, () => {
              return '/';
            });
          },
          hidden: ({ householdResource }) => {
            if (!householdResource) {
              return true;
            }

            return householdResource?.withRelationship(fr.relationships.transitionsDashboardPage.name, () => {
              return false;
            }, () => {
              return true;
            });
          },
        },
        {
          key: 'investor-profile',
          variant: 'link',
          label: 'Investor Profile',
          test: 'investor-profile',
          isSelected: ({ location }) => {
            return location.search.includes('investor-profile');
          },
          to: ({ householdResource }) => {
            return householdResource?.withRelationship(fr.relationships.investorProfilePage.name, (rel) => {
              return buildPageLink(
                rel.link('related'),
                advisorPage,
                { householdId: householdResource?.attr('householdId') },
              );
            }, () => {
              return '/';
            });
          },
          hidden: ({ householdResource }) => {
            if (!householdResource) {
              return true;
            }

            return householdResource?.withRelationship(fr.relationships.investorProfilePage.name, () => {
              return false;
            }, () => {
              return true;
            });
          },
        },
        {
          key: 'client-timeline',
          variant: 'link',
          label: 'Timeline',
          to: ({ householdResource }) => householdTimelineUri(householdResource?.attr('householdId')),
          isSelected: pathMatches(advisorHouseholdTimelinePath),
          hidden: ({ householdResource }) => !householdResource,
        },
        {
          key: 'client-balance-sheet',
          variant: 'link',
          label: 'Balance Sheet',
          test: 'client-balance-sheet',
          isSelected: ({ location }) => {
            return location.search.includes('household-balance-sheet');
          },
          to: getBalanceSheetLink,
          hidden: (...args) => {
            const link = getBalanceSheetLink(...args);
            // If there is no link, then this should be hidden
            return !link;
          },
        },
        {
          key: 'client-edit',
          variant: 'link',
          label: 'Record',
          to: ({ householdResource }) => householdEditRecordPersonalInformationUri(householdResource?.attr('householdId')),
          isSelected: pathMatches(advisorHouseholdQuestionnairePath, { isExact: false }),
          hidden: ({ householdResource }) => !householdResource,
          test: 'client-questionnaire',
        },
        {
          key: 'client-outcomes',
          variant: 'link',
          label: 'Outcomes',
          to: ({ householdResource }) => {
            const planUrl = householdResource?.relationship(fr.relationships.desiredOutcomesSummaryPage.name)?.link('related').url();
            return advisorWhatIfPageUri(planUrl, householdResource?.id());
          },
          isSelected: ({ location }) => pathMatches(advisorWhatIfPage)({ location }) && location.search.includes('desired-outcomes-summary-page'),
          hidden: ({ householdResource }) => !hasRelationship(householdResource, fr.relationships.desiredOutcomesSummaryPage.name),
          test: 'facet-plan-nav-item',
        },
        {
          key: 'client-goals',
          variant: 'link',
          label: 'Goals',
          to: ({ householdResource }) => {
            const planUrl = householdResource?.relationship(fr.relationships.goalsPage.name)?.link('related').url();
            return advisorWhatIfPageUri(planUrl, householdResource?.id());
          },
          isSelected: ({ location }) => pathMatches(advisorWhatIfPage)({ location }) && location.search.includes('goals-page'),
          hidden: ({ householdResource }) => !hasRelationship(householdResource, fr.relationships.goalsPage.name),
          test: 'facet-plan-nav-item',
        },
        {
          key: 'client-files',
          variant: 'link',
          label: 'Files',
          to: ({ householdResource }) => householdSecureFilesUri(householdResource?.attr('householdId')),
          isSelected: pathMatches(advisorSecureFilesPath),
          hidden: ({ householdResource }) => !householdResource,
          test: 'client-files',
        },
        // {
        //   variant: 'subTitle', <-- TO DO: Add ability to create subtitles within sublink lists
        //   label: 'Tools',
        // },
        {
          key: 'client-blueprint',
          variant: 'link',
          label: 'Blueprint',
          to: ({ householdResource }) => householdBlueprintUri(householdResource?.attr('householdId')),
          isSelected: pathMatches(advisorHouseholdBlueprint),
          hidden: ({ householdResource }) => !householdResource,
          test: 'client-blueprint',
        },
        {
          key: 'client-snapshot',
          variant: 'link',
          label: 'Snapshot',
          to: ({ householdResource }) => householdNewPrintUri(householdResource?.attr('householdId')),
          isSelected: pathMatches(advisorHouseholdSnapshot),
          hidden: ({ householdResource }) => !householdResource,
          test: 'client-snapshot',
        },
        {
          key: 'client-plans',
          variant: 'link',
          label: 'Plans',
          to: ({ householdResource }) => householdPlansUri(householdResource?.attr('householdId')),
          isSelected: pathMatches(advisorPlanPath, { isExact: false }),
          hidden: ({ householdResource }) => !householdResource,
        },
        {
          key: 'client-messages-page',
          variant: 'link',
          label: 'Messages',
          to: ({ agentResource, householdResource }) => {
            return buildPageLink(
              agentResource?.relationship(fr.relationships.messagesPage.name).link('related'),
              advisorPageMessages,
              { householdId: householdResource?.attr('householdId') }, // params
            );
          },
          isSelected: pathMatches(advisorMessagesPath),
          hidden: ({ agentResource, householdResource }) => !householdResource || !hasRelationship(agentResource, fr.relationships.messagesPage.name),
        },
        {
          key: 'client-what-if',
          variant: 'link',
          label: 'Roadmaps',
          to: ({ householdResource }) => {
            const planUrl = householdResource?.relationship(fr.relationships.facetPlan.name)?.link('related').url();
            return advisorWhatIfPageUri(planUrl, householdResource?.id());
          },
          isSelected: ({ location }) => pathMatches(advisorWhatIfPage)({ location }) && !location.search.includes('desired-outcomes-summary-page') && !location.search.includes('goals-page'),
          hidden: ({ householdResource }) => !hasRelationship(householdResource, fr.relationships.facetPlan.name),
          test: 'facet-plan-nav-item',
        },
        {
          key: 'client-path-explorer',
          variant: 'link',
          label: 'Path Explorer',
          to: ({ householdResource }) => householdResource?.withRelationship(
            fr.relationships.guidedHouseholdReview.name,
            (rel) => buildPageLink(
              rel.link('related'),
              advisorPage,
              { householdId: householdResource?.id() },
            ),
            () => '',
          ),
          isSelected: isRoadmapExplorer,
          hidden: ({ householdResource }) => !hasRelationship(householdResource, fr.relationships.guidedHouseholdReview.name),
          test: 'path-explorer-nav-item',
        },
        {
          key: 'client-pm2',
          variant: 'link',
          label: 'PM2: Take Action',
          to: ({ householdResource }) => householdResource?.withRelationship(
            fr.relationships.pm2Journey.name,
            (rel) => buildPageLink(
              rel.link('related'),
              advisorPage,
              { householdId: householdResource?.id() },
            ),
            () => '',
          ),
          isSelected: isPM2Journey,
          hidden: ({ householdResource }) => !hasRelationship(householdResource, fr.relationships.pm2Journey.name),
          test: 'pm2-journey-nav-item',
        },
      ],
    },
    {
      key: 'reporting',
      variant: 'dropdown',
      label: 'Reporting',
      to: reporting,
      isSelected: pathMatches(reporting),
      icon: DescriptionIcon,
      hidden: ({ agentResource }) => hasRelationship(agentResource, fr.relationships.reporting.name),
      subLinks: [
        {
          key: 'reporting-analytics',
          variant: 'link',
          label: 'Analytics',
          href: analytics,
        },
        {
          key: 'reporting-meeting-summary',
          variant: 'link',
          label: 'Meeting Summary',
          to: meetingSummary,
          isSelected: pathMatches(meetingSummary),
          hidden: ({ agentResource }) => !hasRelationship(agentResource, relationships.meetingSummary),
        },
      ],
    },
    {
      key: 'tasks',
      variant: 'custom',
      component: TaskListItem,
      isSelected: pathMatches(tasks),
      icon: ListIcon,
      test: 'tasks-nav-link',
    },
    {
      key: 'client-experience',
      variant: 'dropdown',
      label: 'Client Experience',
      hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.clientExperience.name),
      icon: ClientExperience,
      subLinks: [
        {
          key: 'client-experience-accomplishments',
          variant: 'link',
          label: 'Accomplishments',
          to: () => advisorClientExperiencePath,
          isSelected: pathMatches(advisorClientExperiencePath),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.clientExperience.name),
        },
        {
          key: 'client-experience-saves',
          variant: 'link',
          label: 'Saves',
          to: () => advisorClientExperienceSavesPath,
          isSelected: pathMatches(advisorClientExperienceSavesPath),
          hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.escalatedHousehold.name),
        },
      ],
    },
    {
      key: 'investments',
      variant: 'dropdown',
      label: 'Investments',
      icon: AccountBalanceWallet,
      test: 'advisor-investments-nav-link',
      BadgeComponent: InvestmentsBadge,
      hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.investmentsPerformance.name),
      subLinks: [
        {
          key: 'tasks',
          variant: 'link',
          label: 'Client Items',
          to: ({ userResource }) => {
            return taskBoardUri({
              link: userResource?.relationship(fr.relationships.simpleInvestmentOpsActionItems.name).link('related').url(),
            });
          },
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.simpleInvestmentOpsActionItems.name),
          isSelected: pathMatches(tasks),
        },
        {
          key: 'advisor-investments-apex-account-requests',
          variant: 'link',
          label: 'Opened Accounts',
          to: accountRequests,
          isSelected: pathMatches(accountRequests),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.accountRequests.name),
          test: 'apex-account-requests',
        },
        {
          key: 'advisor-investments-portal',
          variant: 'link',
          label: 'Performance',
          to: investments,
          isSelected: pathMatches(investments),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.investmentsPerformance.name),
          test: 'advisor-investments-portal',
        },
        {
          key: 'advisor-rollover-recommendation-questionnaire-page',
          variant: 'link',
          label: 'Rollover Questionnaires',
          to: advisorBulkRolloverRecommendationQuestionnaires,
          isSelected: pathMatches(advisorBulkRolloverRecommendationQuestionnaires),
          hidden: ({
            isInvestmentOps,
            agentResource,
          }) => !isInvestmentOps || !hasRelationship(agentResource, fr.relationships.rolloverRecommendationQuestionnaire.name),
          test: 'advisor-rollover-recommendation-questionnaire-page',
        },
        {
          key: 'advisor-identity-verification-reviews-page',
          variant: 'link',
          label: 'Identity Verification Reviews',
          to: advisorIdentityVerificationReviews,
          isSelected: pathMatches(advisorIdentityVerificationReviews),
          hidden: ({
            agentResource,
          }) => !hasRelationship(agentResource, fr.relationships.identityVerificationReviews.name),
        },
        {
          key: 'advisor-managed-account-allocation-cleanup',
          variant: 'link',
          label: 'Model Assignment',
          to: ({ agentResource }) => genericPageURI(agentResource?.relationship(fr.relationships.managedAccountAllocationCleanup.name).link('related').url()),
          hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.managedAccountAllocationCleanup.name),
        },
        {
          key: 'advisor-managed-account-model-update-request',
          variant: 'custom',
          component: ModelUpdateRequestItem,
          label: 'Model Update Request',
          to: ({ agentResource }) => genericPageURI(agentResource?.relationship(fr.relationships.managedAccountModelUpdateRequestPage.name).link('related').url()),
          hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.managedAccountModelUpdateRequestPage.name),
        },
        {
          key: 'advisor-commentary-time-periods',
          variant: 'link',
          label: 'Commentary Time Period',
          to: ({ userResource }) => genericPageURI(userResource?.relationship(fr.relationships.performanceCommentaryTimePeriodPage.name).link('related').url()),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.performanceCommentaryTimePeriodPage.name),
        },
        {
          key: 'advisor-commentary',
          variant: 'link',
          label: 'Commentary',
          to: ({ userResource }) => genericPageURI(userResource?.relationship(fr.relationships.performanceCommentaryPage.name).link('related').url()),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.performanceCommentaryPage.name),
        },
        {
          key: 'admin-move-managed-account',
          variant: 'link',
          label: 'Re-Household Managed Accounts',
          to: ({ userResource }) => genericPageURI(userResource?.relationship(fr.relationships.moveManagedAccountsToNewHousehold.name).link('related').url()),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.moveManagedAccountsToNewHousehold.name),
        },
        {
          key: 'admin-override-managed-account-household',
          variant: 'link',
          label: 'Override Managed Account Household/Client',
          to: ({ userResource }) => genericPageURI(userResource?.relationship(fr.relationships.overrideManagedAccountHousehold.name).link('related').url()),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.overrideManagedAccountHousehold.name),
        },
      ],
    },
    {
      key: 'curated-content',
      variant: 'link',
      label: 'Curated Content Articles',
      icon: PostAddIcon,
      to: curatedContentArticles,
      isSelected: pathMatches(curatedContentArticles),
      hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.curatedContentArticles.name),
      test: 'curated-content-articles-nav-link',
    },
    {
      key: 'wellness-quiz-admin',
      variant: 'link',
      label: 'Facet Quiz Admin',
      icon: Assessment,
      to: ({ homeResource }) => {
        return homeResource?.withRelationship(fr.relationships.wellnessQuizAdministration.name, (rel) => {
          return genericPageURI(rel.link('related').url());
        }, () => {
          return '/';
        });
      },
      hidden: ({ homeResource }) => {
        return homeResource?.withRelationship(fr.relationships.wellnessQuizAdministration.name, () => {
          return false;
        }, () => {
          return true;
        });
      },
      isSelected: pathMatches('fq', { isExact: false }),
      test: 'fq-quiz-admin',
    },
    {
      key: 'security-settings',
      variant: 'link',
      label: 'Security Settings',
      icon: Lock,
      to: ({ userResource }) => {
        const planUrl = userResource?.relationship(fr.relationships.settingsSectionSecurity.name)?.link('related')?.url();
        return genericPageURI(planUrl, { pagePath: 'security-settings' });
      },
      isSelected: pathMatches('security-settings', { isExact: false }),
      test: 'settings-section-security-nav-item',
    },
    {
      key: 'settings',
      variant: 'dropdown',
      label: 'Settings',
      icon: SettingsIcon,
      test: 'super-user-nav-link',
      hidden: ({ agentResource }) => hasRelationship(agentResource, fr.relationships.plannerSettings.name),
      subLinks: [
        {
          key: 'settings-users-teams',
          variant: 'link',
          label: 'Users & Teams',
          to: settingsUri,
          isSelected: pathMatches(settingsUri),
          test: 'settings-users-teams-nav-link',
          hidden: ({ hasSuperUserRole }) => !hasSuperUserRole,
        },
        {
          key: 'settings-features',
          variant: 'link',
          label: 'Features',
          to: featuresUri,
          isSelected: pathMatches(featuresUri),
          hidden: ({ hasSuperUserRole, homeResource }) => !hasSuperUserRole || hasRelationship(homeResource, fr.relationships.featureToggleHierarchy.name),
        },
        {
          key: 'settings-feature-toggle-hierarchy',
          variant: 'link',
          label: 'Feature Toggles',
          to: ({ homeResource }) => {
            const planUrl = homeResource?.relationship(fr.relationships.featureToggleHierarchy.name)?.link('related')?.url();
            return genericPageURI(planUrl, { pagePath: 'feature-toggles' });
          },
          isSelected: pathMatches('feature-toggles', { isExact: false }),
          hidden: ({ homeResource }) => {
            return !hasRelationship(homeResource, fr.relationships.featureToggleHierarchy.name);
          },
          test: 'settings-feature-toggle-hierarchy',
        },
        {
          key: 'settings-templates',
          variant: 'link',
          label: 'Templates',
          to: ({ agentResource }) => {
            return agentResource?.withRelationship(
              fr.relationships.templateManagerPage.name,
              (rel) => buildPageLink(
                rel.link('related'),
                advisorTemplateManagerPage,
              ),
              () => '',
            );
          },
          isSelected: ({ agentResource }) => {
            return agentResource?.withRelationship(
              fr.relationships.templateManagerPage.name,
              () => pathMatches(advisorTemplateManagerPage, { exact: false }),
              () => false,
            );
          },
          hidden: ({ hasSuperUserRole }) => !hasSuperUserRole,
        },
        {
          key: 'settings-assumptions',
          variant: 'link',
          label: 'Global Facet Plan Assumptions',
          to: assumptionsAdminUri,
          isSelected: pathMatches(assumptionsAdminUri),
          hidden: ({ userResource }) => !hasRelationship(userResource, fr.relationships.facetPlanAssumptions.name) || hasRelationship(userResource, fr.relationships.facetPlanAssumptionByDate.name),
        },
        {
          key: 'settings-global-assumptions-by-date',
          variant: 'link',
          label: 'Global Facet Plan Assumptions By Date',
          to: ({ userResource }) => {
            const planUrl = userResource?.relationship(fr.relationships.facetPlanAssumptionByDate.name)?.link('related')?.url();
            return genericPageURI(planUrl, { pagePath: 'global-assumptions-by-date' });
          },
          isSelected: pathMatches('global-assumptions-by-date', { isExact: false }),
          hidden: ({ userResource }) => {
            return !hasRelationship(userResource, fr.relationships.facetPlanAssumptionByDate.name);
          },
          test: 'settings-global-assumptions-by-date',
        },
        {
          key: 'settings-preferences',
          variant: 'link',
          label: 'Preferences',
          to: preferences,
          isSelected: pathMatches(preferences),
          test: 'settings-preferences-nav-link',
        },
        {
          key: 'settings-linking-attempts',
          variant: 'link',
          label: 'Account Linking Attempts',
          to: ({ agentResource }) => {
            const link = agentResource?.relationship(fr.relationships.linkingAttempts.name).link('related').url();
            return genericPageURI(link, { pageType: 'linkingAttempts' });
          },
          isSelected: ({ location }) => {
            return location.search.includes('linkingAttempts');
          },
        },
        {
          key: 'settings-aggregator-success-rates',
          variant: 'link',
          label: 'Account Linking Success Rate',
          to: ({ agentResource }) => {
            const link = agentResource?.relationship(fr.relationships.financialInstitutionSuccessRate.name).link('related').url();
            return genericPageURI(link, { pageType: 'institutionSuccessRates' });
          },
          isSelected: ({ location }) => {
            return location.search.includes('institutionSuccessRates');
          },
        },
        {
          key: 'settings-generated-views',
          variant: 'link',
          label: 'My Views',
          to: ({ agentResource }) => {
            const link = agentResource?.relationship(fr.relationships.generatedView.name).link('related').url();
            return genericPageURI(link, { pageType: 'generatedViews' });
          },
          isSelected: ({ location }) => {
            return location.search.includes('generatedViews');
          },
          hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.generatedView.name),
        },
        {
          key: 'settings-planner-transition',
          variant: 'link',
          label: 'Planner Transitions',
          to: ({ agentResource }) => {
            const link = agentResource?.relationship(fr.relationships.plannerTransitionHouseholdsPage.name).link('related').url();
            return genericPageURI(link, { pageType: 'plannerTransitionHousehold' });
          },
          isSelected: ({ location }) => {
            return location.search.includes('plannerTransitionHousehold');
          },
          hidden: ({ agentResource }) => !hasRelationship(agentResource, fr.relationships.plannerTransitionHouseholdsPage.name),
        },
        {
          key: 'admin-lead-page',
          variant: 'link',
          label: 'Create Lead',
          to: ({ homeResource }) => {
            const pageUrl = homeResource?.relationship(fr.relationships.leadPage.name)?.link('related')?.url();
            return genericPageURI(pageUrl, { pagePath: 'lead' });
          },
          isSelected: pathMatches('lead', { isExact: false }),
          test: 'admin-lead-page',
          hidden: ({ homeResource }) => {
            return homeResource?.withRelationship(fr.relationships.leadPage.name, () => false, () => true);
          },
        },
      ],
    },
  ],
};

export default advisorSchema;
